import {
  ButtonType,
  DonateButtonFormData,
} from '../../../types/givingCheckoutForm';
import React, { FunctionComponent, useMemo } from 'react';

import CodeBlock from '../CodeBlock/CodeBlock';
import CodeBlockSingleLine from '../CodeBlockSingleLine/CodeBlockSingleLine';
import { HelpLink } from './HelpLink';
import config from '../../config';
import { useCharityData } from '../../context/charityContext';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';

type Props = {
  buttonType: ButtonType;
  codeBlockType: 'single' | 'multi';
  widgetType: 'donateButton' | 'attachCheckout';
  linkId?: string;
  isDefault?: boolean;
};

const WidgetButtonCode: FunctionComponent<Props> = ({
  buttonType,
  codeBlockType,
  widgetType,
  linkId,
  isDefault = false,
}) => {
  const copyToClipboard = useCopyToClipboard();
  const { t } = useTranslation();
  const { charityId, charity } = useCharityData();

  const [includePaymentLogos, buttonSize, buttonColor] = useWatch<
    DonateButtonFormData,
    ['includePaymentLogos', 'buttonSize', 'buttonColor']
  >({ name: ['includePaymentLogos', 'buttonSize', 'buttonColor'] });

  const donateButtonType = useMemo(() => {
    if (buttonType === 'standard') return buttonType;
    return `justgiving${buttonSize === 'large' ? 'Large' : 'Small'}`;
  }, [buttonSize, buttonType]);

  const customColour = useMemo(() => {
    if (buttonType !== 'standard' || !buttonColor) return '';
    return `data-customColour="${buttonColor}"`;
  }, [buttonColor, buttonType]);

  const linkIdOrCharityId = useMemo(() => {
    if (isDefault) return `data-charityId="${charityId}"`;
    return `data-linkId="${linkId}"`;
  }, [charityId, isDefault, linkId]);

  const donateButtonCode = `
<script src="${config.WIDGET_URL}" data-version="${
    config.WIDGET_VERSION
  }" data-widgetType="${widgetType}" data-linkType="givingCheckout" data-donateButtonType="${donateButtonType}" ${linkIdOrCharityId} data-marketCode="${
    charity?.operatingCompanyCode ?? ''
  }" ${customColour} data-showPaymentLogos="${includePaymentLogos.toString()}" data-popupCheckout="true" type="text/javascript"></script>
`;

  return (
    <>
      {codeBlockType === 'single' ? (
        <CodeBlockSingleLine
          textContents={donateButtonCode}
          buttonId="addDonateButtonCopy"
          codeId="widget-code"
        />
      ) : (
        <>
          {' '}
          <CodeBlock
            id="widget-code"
            ariaLabel={t('linkDetails.buttonCode.label')}
            dataQa="widget-code"
          >
            {donateButtonCode}
          </CodeBlock>
          <button
            id="add-donate-button--copy"
            className="jg-btn jg-btn--secondary gc-button-width"
            onClick={() =>
              copyToClipboard('add-donate-button--copy', 'widget-code')
            }
          >
            {t('common.copy')}
          </button>
        </>
      )}
      <HelpLink />
    </>
  );
};

export default WidgetButtonCode;
